"use client";

import React, { FC } from "react";
import { getLineItemForProduct } from "@/utils/cart-utils";
import { useI18n } from "@/i18n/client";
import useStoreKeys from "@/hooks/useStoreKeys";
import { useCartContext } from "@/providers/CartContextProvider";
import Modal from "@/components/Modal/Modal";
import Button from "@/components/UI/Button/Button";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import FormattedPrice from "@/components/UI/FormattedPrice/FormattedPrice";
import SearchSpringProductRecommendationCarousel from "@/components/SearchSpringProductRecommendationCarousel/SearchSpringProductRecommendationCarousel";
import Tick from "@/app/assets/images/svg/check.svg";
import styles from "./AddToCartConfirmation.module.scss";

import mapProductOptions from "@/hooks/mapProductOptions";

interface Props {
	addedToCartSettings: {
		title: string;
		productRecommendationId: string;
	};
}

const AddToCartConfirmation: FC<Props> = ({ addedToCartSettings }) => {
	const { cart, confirmationProduct, setConfirmationProduct } =
		useCartContext();
	const { store, lang } = useStoreKeys();
	const t = useI18n();

	if (!confirmationProduct) {
		return;
	}

	const lineItem = getLineItemForProduct(cart, confirmationProduct);

	// Only show on first addition to the cart
	if (!lineItem || lineItem.quantity > 1) return;

	const handleClose = () => {
		setConfirmationProduct(undefined);
	};

	const title = (
		<div className={styles.header}>
			<div className={styles.circle}>
				<Tick />
			</div>
			{t("cart.addedToCart")}
		</div>
	);

	const productSpecifications = mapProductOptions([confirmationProduct]);

	return (
		<Modal
			title={title}
			isOpen
			toggleModal={handleClose}
			contentClassName={styles.container}
		>
			<div className={styles.item}>
				<CompressedImage
					src={lineItem.image_url}
					height={100}
					width={100}
					altText={lineItem.name}
					containerClass={styles.image}
					withNextImage={false}
				/>
				<div className={styles.details}>
					<span className={styles.name}>{lineItem.name}</span>
					<span className={styles.sku}>{lineItem.sku}</span>
					{productSpecifications && productSpecifications.length > 0 && (
						<div className={styles.specs}>
							{productSpecifications?.map((spec) => (
								<div key={spec.label} className={styles.spec}>
									<span className={styles.label}>{spec.label}:</span>
									<span className={styles.value}>{spec.options[0].label}</span>
								</div>
							))}
						</div>
					)}
					<span className={styles.price}>
						{lineItem.quantity} x <FormattedPrice price={lineItem.sale_price} />
					</span>
				</div>
			</div>
			<SearchSpringProductRecommendationCarousel
				searchSpringId={addedToCartSettings.productRecommendationId}
				title={addedToCartSettings.title}
				placementProductId=''
			/>
			<div className={styles.actions}>
				<Button variant='primary' as='link' href={`/${store}/${lang}/cart`}>
					Cart
				</Button>
			</div>
		</Modal>
	);
};

export default AddToCartConfirmation;
